/**
 * Rascunhos das novas solicitações, guardados só no navegador (localStorage), um por usuário e por
 * processo. Só os campos — arquivos (File/Blob) nunca entram, o usuário anexa de novo ao retomar.
 *
 * Tolerância a falhas:
 * - toda leitura/escrita é protegida: localStorage indisponível (modo privado, bloqueado), cheio
 *   ou com JSON corrompido nunca derruba o formulário — a função só devolve o erro;
 * - antes de sobrescrever, o rascunho atual é copiado para uma chave reserva; se a gravação nova
 *   falhar no meio ou o conteúdo principal estiver corrompido, a leitura cai na reserva;
 * - depois de gravar, o conteúdo é relido e comparado (detecta gravação silenciosamente perdida);
 * - o envelope tem versão; versões desconhecidas são ignoradas em vez de aplicadas às cegas.
 */
const PREFIXO = "mydash.solicitacoes.rascunho";
const VERSAO = 1;

function chave(usuarioId, tipoProcesso) {
    return `${PREFIXO}.v${VERSAO}.${usuarioId || "anonimo"}.${tipoProcesso}`;
}

function chaveReserva(usuarioId, tipoProcesso) {
    return `${chave(usuarioId, tipoProcesso)}.reserva`;
}

// Arquivos e funções não são serializáveis (e anexos não fazem parte do rascunho).
function substituirNaoSerializaveis(_chave, valor) {
    if (typeof Blob !== "undefined" && valor instanceof Blob) return undefined;
    if (typeof valor === "function") return undefined;
    return valor;
}

function lerEnvelope(chaveStorage) {
    try {
        const bruto = window.localStorage.getItem(chaveStorage);
        if (!bruto) return null;
        const envelope = JSON.parse(bruto);
        if (!envelope || envelope.versao !== VERSAO || typeof envelope.dados !== "object" || envelope.dados === null) return null;
        return envelope;
    } catch {
        return null;
    }
}

/** `{ dados, salvoEm }` do rascunho salvo, ou `null` (inexistente, ilegível ou de versão desconhecida). */
export function lerRascunho(usuarioId, tipoProcesso) {
    return lerEnvelope(chave(usuarioId, tipoProcesso)) || lerEnvelope(chaveReserva(usuarioId, tipoProcesso));
}

/** `{ ok: true, salvoEm }` ou `{ ok: false, motivo }` — nunca lança. */
export function salvarRascunho(usuarioId, tipoProcesso, dados) {
    const salvoEm = new Date().toISOString();
    let conteudo;
    try {
        conteudo = JSON.stringify({ versao: VERSAO, tipoProcesso, salvoEm, dados }, substituirNaoSerializaveis);
    } catch {
        return { ok: false, motivo: "não foi possível preparar os dados do formulário para salvar" };
    }

    const principal = chave(usuarioId, tipoProcesso);
    try {
        const atual = window.localStorage.getItem(principal);
        if (atual) window.localStorage.setItem(chaveReserva(usuarioId, tipoProcesso), atual);
        window.localStorage.setItem(principal, conteudo);
        if (window.localStorage.getItem(principal) !== conteudo) {
            return { ok: false, motivo: "o navegador não confirmou a gravação" };
        }
        return { ok: true, salvoEm };
    } catch (erro) {
        const cheio = erro?.name === "QuotaExceededError" || erro?.code === 22;
        return {
            ok: false,
            motivo: cheio
                ? "o espaço de armazenamento do navegador está cheio"
                : "o navegador não permite salvar dados (modo privado ou armazenamento bloqueado)",
        };
    }
}

export function excluirRascunho(usuarioId, tipoProcesso) {
    try {
        window.localStorage.removeItem(chave(usuarioId, tipoProcesso));
        window.localStorage.removeItem(chaveReserva(usuarioId, tipoProcesso));
    } catch {
        // Sem acesso ao storage — não há o que remover.
    }
}

/**
 * Restauração tolerante a mudanças no formulário: parte do estado inicial atual e só aproveita do
 * rascunho os campos que ainda existem, com tipo compatível. Campo novo fica com o padrão; campo
 * removido ou com tipo diferente (rascunho de uma versão antiga do formulário) é ignorado.
 */
export function mesclarComPadrao(padrao, salvo) {
    if (!salvo || typeof salvo !== "object" || Array.isArray(salvo)) return padrao;
    const resultado = { ...padrao };
    Object.keys(padrao).forEach(campo => {
        if (!(campo in salvo)) return;
        const valorPadrao = padrao[campo];
        const valorSalvo = salvo[campo];
        const compativel = valorPadrao === null || valorPadrao === undefined || valorSalvo === null
            || (Array.isArray(valorPadrao) ? Array.isArray(valorSalvo) : typeof valorPadrao === typeof valorSalvo);
        if (compativel) resultado[campo] = valorSalvo;
    });
    return resultado;
}

/** Lista do rascunho só se for mesmo uma lista (e só com itens objeto), senão vazia. */
export function listaDoRascunho(valor) {
    return Array.isArray(valor) ? valor.filter(item => item && typeof item === "object") : [];
}
